// Iter-200c — Public, no-auth payment declaration page (rev. BAIM brand).
// Adds:
//   • Declarant identity (first/last/email — required).
//   • Proof attachment: file upload (PDF/PNG/JPG ≤5MB) OR pasted text.
//   • Required attestation checkbox.
//   • Trust seal under CTA + BAIM legal footer (RC/ICE/IF/Patente/CNSS).
//   • Link to /privacy page (RGPD).
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Loader2, Check, AlertCircle, CheckCircle2, Banknote, Mail, ShieldCheck,
  Lock, Upload, FileText, X as XIcon,
} from "lucide-react";
import { BAIMPoweredByCompact, BAIMPoweredByExpanded } from "@/components/BAIMPoweredBy";

const API = process.env.REACT_APP_BACKEND_URL;

const BRAND = {
  navy: "#1E3A5F",
  navyHover: "#2A4570",
  creamBg: "#FAF6EE",
  creamBg2: "#F5EBD6",
  creamBorder: "#EDE0C6",
  orange: "#D97A2A",
};

const fmtMoney = (n, ccy) =>
  `${Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${ccy || ""}`;

export default function PaymentDeclare() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [proofMeta, setProofMeta] = useState(null);  // {proof_id, filename, size_bytes, mime}
  const [form, setForm] = useState({
    declared_date: new Date().toISOString().slice(0, 10),
    declared_amount: "",
    payment_method: "Virement",
    payment_reference: "",
    comment: "",
    declarant_first_name: "",
    declarant_last_name: "",
    declarant_email: "",
    proof_text: "",
    attestation: false,
  });
  const fileInputRef = useRef(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios.get(`${API}/api/public/payment-declarations/${token}`)
      .then((r) => {
        if (!alive) return;
        setInvoice(r.data);
        setForm((f) => ({ ...f, declared_amount: String(r.data.total_ttc || "") }));
      })
      .catch((e) => alive && setError(e?.response?.data?.detail || "Impossible de charger la facture."))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [token]);

  const uploadProof = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError("Fichier trop volumineux (>5 MB).");
      return;
    }
    setError(null);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(
        `${API}/api/public/payment-declarations/${token}/upload-proof`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setProofMeta(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Échec de l'envoi du fichier.");
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const r = await axios.post(`${API}/api/public/payment-declarations/${token}`, {
        declared_date: form.declared_date,
        declared_amount: parseFloat(form.declared_amount),
        payment_method: form.payment_method,
        payment_reference: form.payment_reference.trim(),
        comment: form.comment.trim() || null,
        declarant_first_name: form.declarant_first_name.trim(),
        declarant_last_name: form.declarant_last_name.trim(),
        declarant_email: form.declarant_email.trim(),
        proof_text: form.proof_text.trim() || null,
        proof_file_id: proofMeta?.proof_id || null,
        attestation: form.attestation,
      });
      setResult(r.data);
      // Iter-206j — scroll to top so the success card is the first thing
      // the user sees (previously the page kept the scroll position of the
      // submit button, hiding the confirmation above the fold).
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setError(e?.response?.data?.detail || "Erreur lors de l'envoi.");
    } finally {
      setSubmitting(false);
    }
  };

  // Form validity for the submit button.
  const canSubmit = (
    !submitting &&
    form.payment_reference.trim() &&
    form.declared_amount &&
    form.declarant_first_name.trim() &&
    form.declarant_last_name.trim() &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.declarant_email.trim()) &&
    form.attestation
  );

  if (loading) {
    return <Shell><div className="flex items-center gap-2 text-zinc-600"><Loader2 size={16} className="animate-spin" /> Chargement...</div></Shell>;
  }
  if (error && !invoice) {
    return (
      <Shell>
        <div className="flex items-start gap-3 rounded-xl border p-6 bg-white shadow-sm" style={{ borderColor: "#FCA5A5" }}>
          <div className="rounded-full p-2 shrink-0" style={{ background: "#FEE2E2" }}>
            <AlertCircle size={22} style={{ color: "#991B1B" }} />
          </div>
          <div>
            <div className="font-heading text-lg" style={{ color: BRAND.navy }}>Lien invalide ou expiré</div>
            <div className="text-sm mt-1" style={{ color: BRAND.navy, opacity: 0.8 }}>{error}</div>
            <div className="text-xs mt-3" style={{ color: BRAND.navy, opacity: 0.6 }}>
              Si vous pensez qu'il s'agit d'une erreur, écrivez à{" "}
              <a href="mailto:facturation@ba-inmotion.com" className="underline" style={{ color: BRAND.orange }}>facturation@ba-inmotion.com</a>.
            </div>
          </div>
        </div>
      </Shell>
    );
  }
  if (result) {
    return (
      <Shell omitCompactPromo>
        <div className="flex items-start gap-3 rounded-xl border p-6 bg-white shadow-sm" style={{ borderColor: "#A7E3C1" }} data-testid="payment-declare-success">
          <div className="rounded-full p-2 shrink-0" style={{ background: "#D1FAE5" }}>
            <CheckCircle2 size={28} style={{ color: "#047857" }} />
          </div>
          <div>
            <div className="font-heading text-xl" style={{ color: BRAND.navy }}>
              {result.auto_paid ? "Paiement enregistré, merci !" : "Déclaration reçue, merci !"}
            </div>
            <div className="text-sm mt-2 leading-relaxed" style={{ color: BRAND.navy, opacity: 0.85 }}>{result.message}</div>
            <div className="text-xs mt-4" style={{ color: BRAND.navy, opacity: 0.6 }}>
              Une copie de cette déclaration vient d'être envoyée à BAIM. Vous pouvez fermer cette page.
            </div>
          </div>
        </div>
        {/* Iter-200f — Promo BAIM élargie sur la page de succès (avec carrousel). */}
        <BAIMPoweredByExpanded />
      </Shell>
    );
  }
  if (invoice?.already_paid) {
    return (
      <Shell>
        <div className="flex items-start gap-3 rounded-xl border p-6 bg-white shadow-sm" style={{ borderColor: "#BFDBFE" }}>
          <div className="rounded-full p-2 shrink-0" style={{ background: "#DBEAFE" }}>
            <CheckCircle2 size={24} style={{ color: "#1D4ED8" }} />
          </div>
          <div>
            <div className="font-heading text-lg" style={{ color: BRAND.navy }}>Facture déjà marquée comme réglée</div>
            <div className="text-sm mt-2" style={{ color: BRAND.navy, opacity: 0.85 }}>
              La facture <strong>{invoice.invoice_number}</strong> a été enregistrée comme payée le {invoice.paid_at}. Merci !
            </div>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      {invoice?.preview && (
        <div
          className="rounded-xl border-2 border-dashed p-4 flex items-start gap-3 mb-2"
          style={{ borderColor: BRAND.orange, background: "rgba(217,122,42,0.08)" }}
          data-testid="payment-declare-preview-banner"
        >
          <div className="rounded-full p-2 shrink-0" style={{ background: BRAND.orange }}>
            <ShieldCheck size={18} color="#fff" />
          </div>
          <div>
            <div className="font-heading text-base" style={{ color: BRAND.navy }}>MODE APERÇU — Simulation BAIM</div>
            <div className="text-xs mt-1 leading-relaxed" style={{ color: BRAND.navy, opacity: 0.85 }}>
              Cette page est une simulation. Toutes les actions (upload de pièce, soumission) fonctionnent normalement mais <strong>aucune donnée n'est persistée</strong> : la facture est en mémoire, le déclarant fictif, et le token expire dans 15 minutes.
            </div>
          </div>
        </div>
      )}
      {/* Invoice info card */}
      <div className="rounded-xl bg-white shadow-sm border p-5" style={{ borderColor: BRAND.creamBorder }}>
        <div className="text-[10px] uppercase tracking-[0.2em] font-mono" style={{ color: BRAND.navy, opacity: 0.55 }}>Facture</div>
        <div className="font-heading text-2xl mt-1" style={{ color: BRAND.navy }} data-testid="payment-declare-invoice-number">
          {invoice.invoice_number}
        </div>
        <div className="text-sm mt-1" style={{ color: BRAND.navy, opacity: 0.75 }}>
          {invoice.client_name}{invoice.mission_title ? ` · ${invoice.mission_title}` : ""}
        </div>
        <div className="grid grid-cols-2 gap-3 mt-5 text-sm">
          <Field label="Montant total TTC" value={fmtMoney(invoice.total_ttc, invoice.currency)} icon={Banknote} />
          <Field label="Date d'échéance" value={invoice.due_date || "—"} />
        </div>
        {invoice.pending_declaration && (
          <div className="mt-4 flex items-start gap-2 rounded-md border p-3 text-xs" style={{ background: "#FBE6D2", borderColor: "#E89A5C", color: "#7C3F00" }}>
            <AlertCircle size={13} className="shrink-0 mt-0.5" />
            <div>Une déclaration de paiement pour cette facture a déjà été soumise le {invoice.pending_declaration.declared_at?.slice(0, 10)} et est en cours de validation par BAIM.</div>
          </div>
        )}
      </div>

      {/* Declarant identity */}
      <SectionCard title="Vos coordonnées">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <InputField label="Prénom *" value={form.declarant_first_name} onChange={(v) => setForm({ ...form, declarant_first_name: v })} testid="declarant-first-name" required />
          <InputField label="Nom *" value={form.declarant_last_name} onChange={(v) => setForm({ ...form, declarant_last_name: v })} testid="declarant-last-name" required />
        </div>
        <div className="mt-3">
          <InputField label="Adresse email *" type="email" value={form.declarant_email} onChange={(v) => setForm({ ...form, declarant_email: v })} testid="declarant-email" required placeholder="vous@entreprise.com" />
        </div>
      </SectionCard>

      {/* Payment details */}
      <SectionCard title="Détails du paiement">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <InputField label="Date opération *" type="date" value={form.declared_date} onChange={(v) => setForm({ ...form, declared_date: v })} testid="declared-date" required />
          <InputField label={`Montant payé (${invoice.currency}) *`} type="number" step="0.01" value={form.declared_amount} onChange={(v) => setForm({ ...form, declared_amount: v })} testid="declared-amount" required />
          <SelectField label="Mode de paiement" value={form.payment_method} options={invoice.allowed_methods} onChange={(v) => setForm({ ...form, payment_method: v })} testid="payment-method" />
          <InputField label="Référence (n° virement, Swift) *" value={form.payment_reference} onChange={(v) => setForm({ ...form, payment_reference: v })} testid="payment-reference" required placeholder="VIR-202605-001" />
        </div>
        <div className="mt-3">
          <Label>Commentaire (optionnel)</Label>
          <textarea value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} rows={2} data-testid="payment-comment"
            className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2"
            style={{ borderColor: BRAND.creamBorder, color: BRAND.navy }} placeholder="Ex: paiement partiel — solde le 15/06" />
        </div>
      </SectionCard>

      {/* Proof of payment */}
      <SectionCard title="Pièce justificative">
        <div className="text-[12px] mb-3 leading-relaxed" style={{ color: BRAND.navy, opacity: 0.7 }}>
          Joignez le scan ou photo du virement (PDF, PNG, JPG ≤ 5 MB), <strong>ou</strong> copiez-collez les détails au format texte ci-dessous.
        </div>
        <div className="rounded-lg border-2 border-dashed p-4" style={{ borderColor: BRAND.creamBorder, background: "#FCFAF4" }}>
          {!proofMeta ? (
            <div className="flex flex-col items-center text-center">
              <button onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="proof-upload-trigger"
                className="inline-flex items-center gap-2 px-4 h-10 text-sm font-medium rounded-md border bg-white hover:bg-zinc-50 transition disabled:opacity-50"
                style={{ borderColor: BRAND.creamBorder, color: BRAND.navy }}>
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                {uploading ? "Envoi en cours..." : "Choisir un fichier"}
              </button>
              <input ref={fileInputRef} type="file" accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg" className="hidden"
                onChange={(e) => uploadProof(e.target.files?.[0])} data-testid="proof-file-input" />
              <div className="mt-2 text-[10px] font-mono" style={{ color: BRAND.navy, opacity: 0.55 }}>
                PDF, PNG ou JPG • 5 MB max
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 text-sm">
              <FileText size={18} style={{ color: BRAND.orange }} />
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate" style={{ color: BRAND.navy }} data-testid="proof-filename">{proofMeta.filename}</div>
                <div className="text-[11px] font-mono" style={{ color: BRAND.navy, opacity: 0.6 }}>{(proofMeta.size_bytes / 1024).toFixed(1)} KB · {proofMeta.mime}</div>
              </div>
              <button onClick={() => setProofMeta(null)} data-testid="proof-remove"
                className="p-1.5 rounded hover:bg-zinc-100" title="Retirer la pièce jointe">
                <XIcon size={14} style={{ color: BRAND.navy }} />
              </button>
            </div>
          )}
        </div>
        <div className="mt-3">
          <Label>Ou collez les informations au format texte (optionnel)</Label>
          <textarea value={form.proof_text} onChange={(e) => setForm({ ...form, proof_text: e.target.value })} rows={3} data-testid="proof-text"
            className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 font-mono"
            style={{ borderColor: BRAND.creamBorder, color: BRAND.navy }}
            placeholder="Ex: Virement BCE émis le 20/05/2026, montant 5000 EUR, réf VIR-9088-X, swift XYZ..." />
        </div>
      </SectionCard>

      {/* Error + Attestation + Submit */}
      <div className="rounded-xl bg-white shadow-sm border p-5 mt-4" style={{ borderColor: BRAND.creamBorder }}>
        {/* Iter-200c — required attestation checkbox replaces the previous
            informational footer. */}
        <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="attestation-label">
          <input type="checkbox" checked={form.attestation} onChange={(e) => setForm({ ...form, attestation: e.target.checked })}
            data-testid="attestation-checkbox"
            className="mt-1 w-4 h-4 rounded border-2 cursor-pointer" style={{ accentColor: BRAND.navy, borderColor: BRAND.creamBorder }} />
          <div className="text-sm leading-relaxed" style={{ color: BRAND.navy }}>
            <span className="font-medium">Je déclare l'exactitude des informations renseignées.</span>{" "}
            <span style={{ opacity: 0.7 }}>*</span>
          </div>
        </label>

        {error && (
          <div className="mt-4 text-sm flex items-start gap-2 rounded-md p-2.5" style={{ background: "#FEE2E2", color: "#991B1B" }}>
            <AlertCircle size={14} className="shrink-0 mt-0.5" /><div>{error}</div>
          </div>
        )}

        <div className="mt-5 flex flex-col items-center gap-3">
          <button onClick={submit} disabled={!canSubmit} data-testid="payment-declare-submit"
            className="inline-flex items-center gap-2 px-7 h-12 text-white text-sm font-semibold rounded-lg shadow-sm transition disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            style={{ background: submitting ? "#5B7BA3" : BRAND.navy, letterSpacing: "0.02em" }}
            onMouseOver={(e) => { if (canSubmit) e.currentTarget.style.background = BRAND.navyHover; }}
            onMouseOut={(e) => { if (canSubmit) e.currentTarget.style.background = BRAND.navy; }}>
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
            Déclarer le paiement
          </button>
          {/* Iter-200c — Trust seal under the CTA */}
          <div className="flex flex-col items-center gap-1.5 mt-1" data-testid="trust-seal">
            <div className="flex items-center gap-1.5 text-[11px]" style={{ color: BRAND.navy, opacity: 0.7 }}>
              <ShieldCheck size={13} style={{ color: "#10b981" }} />
              <span className="font-semibold">Lien sécurisé</span>
              <span style={{ opacity: 0.4 }}>·</span>
              <Lock size={11} />
              <span>Confidentiel</span>
              <span style={{ opacity: 0.4 }}>·</span>
              <span>Exclusif à cette facture</span>
              <span style={{ opacity: 0.4 }}>·</span>
              <span>Expire dans 90 jours</span>
            </div>
            <div className="text-[10px]" style={{ color: BRAND.navy, opacity: 0.55 }}>
              Vos informations sont protégées selon le{" "}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: BRAND.orange }} data-testid="privacy-link">
                RGPD et notre politique de confidentialité
              </a>.
            </div>
          </div>
        </div>
      </div>
    </Shell>
  );
}

function Shell({ children, omitCompactPromo }) {
  return (
    <div className="min-h-screen" style={{ background: `linear-gradient(180deg, ${BRAND.creamBg} 0%, ${BRAND.creamBg2} 100%)` }}>
      <div className="max-w-2xl mx-auto px-4 py-10">
        <div className="flex items-center gap-3 mb-8">
          <img src="/logo.png" alt="BAIM" className="w-12 h-12 rounded-lg shadow-sm object-cover" data-testid="payment-declare-logo" />
          <div>
            <div className="font-heading font-bold text-xl" style={{ color: BRAND.navy, letterSpacing: "-0.01em" }}>BAIM</div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-mono" style={{ color: BRAND.navy, opacity: 0.55 }}>Business Agility in Motion</div>
          </div>
          <div className="ml-auto text-right">
            <div className="text-[10px] uppercase tracking-widest font-mono" style={{ color: BRAND.navy, opacity: 0.55 }}>Espace client</div>
            <div className="text-[13px]" style={{ color: BRAND.navy }}>Déclaration de paiement</div>
          </div>
        </div>
        {children}
        {/* Iter-200f — Promo BAIM compacte visible sur le formulaire et états intermédiaires.
            (La version élargie avec carrousel est rendue séparément sur la page de succès.) */}
        {!omitCompactPromo && <BAIMPoweredByCompact />}
        {/* Iter-200c — BAIM legal footer */}
        <BAIMLegalFooter />
      </div>
    </div>
  );
}

function BAIMLegalFooter() {
  return (
    <footer className="mt-10 pt-6 border-t text-center" style={{ borderColor: BRAND.creamBorder }} data-testid="baim-legal-footer">
      <div className="text-[11px] flex items-center justify-center gap-1.5 mb-3" style={{ color: BRAND.navy, opacity: 0.7 }}>
        <Mail size={11} /> Questions ?{" "}
        <a href="mailto:facturation@ba-inmotion.com" className="underline hover:no-underline" style={{ color: BRAND.orange }}>
          facturation@ba-inmotion.com
        </a>
      </div>
      <div className="text-[10px] font-mono leading-relaxed" style={{ color: BRAND.navy, opacity: 0.6 }}>
        <strong>BAIM</strong> — Business Agility in Motion · Casablanca, Maroc
      </div>
      <div className="text-[10px] font-mono mt-1" style={{ color: BRAND.navy, opacity: 0.55 }}>
        RC&nbsp;:&nbsp;710919 · ICE&nbsp;:&nbsp;003887289000010 · IF&nbsp;:&nbsp;71754267 · Patente&nbsp;:&nbsp;33304151 · CNSS&nbsp;:&nbsp;6661461
      </div>
      <div className="text-[10px] mt-3" style={{ color: BRAND.navy, opacity: 0.5 }}>
        © BAIM · ba-inmotion.com · Confidentiel ·{" "}
        <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: BRAND.orange }}>
          Confidentialité & RGPD
        </a>
      </div>
    </footer>
  );
}

function SectionCard({ title, children }) {
  return (
    <div className="rounded-xl bg-white shadow-sm border p-5 mt-4" style={{ borderColor: BRAND.creamBorder }}>
      <div className="font-heading text-lg mb-3" style={{ color: BRAND.navy }}>{title}</div>
      {children}
    </div>
  );
}

function Field({ label, value, icon: Icon }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] font-mono mb-0.5 flex items-center gap-1" style={{ color: BRAND.navy, opacity: 0.6 }}>
        {Icon ? <Icon size={11} /> : null} {label}
      </div>
      <div className="font-medium" style={{ color: BRAND.navy }}>{value}</div>
    </div>
  );
}

function Label({ children }) {
  return <div className="text-[10px] uppercase tracking-[0.2em] font-mono mb-1" style={{ color: BRAND.navy, opacity: 0.65 }}>{children}</div>;
}

function InputField({ label, value, onChange, type = "text", required, testid, step, placeholder }) {
  return (
    <div>
      <Label>{label}</Label>
      <input type={type} value={value} step={step} onChange={(e) => onChange(e.target.value)} required={required}
        placeholder={placeholder} data-testid={testid}
        className="w-full h-11 border rounded-md px-3 text-sm focus:outline-none focus:ring-2"
        style={{ borderColor: BRAND.creamBorder, color: BRAND.navy }} />
    </div>
  );
}

function SelectField({ label, value, options, onChange, testid }) {
  return (
    <div>
      <Label>{label}</Label>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full h-11 border rounded-md px-2 text-sm bg-white focus:outline-none focus:ring-2"
        style={{ borderColor: BRAND.creamBorder, color: BRAND.navy }}>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
